import { createFileRoute, redirect } from '@tanstack/react-router';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ArrowLeft, Eye, EyeOff, Loader2, Ship } from 'lucide-react';
import { useMutationLogin } from '@/api/queries/auth';
import useAuthStore from '@/store/auth';
import apiConfig from '@/config/url-api';
import toast from 'react-hot-toast';
import { resetSessionExpired } from '@/api/axios/handleUnauthorized';

const loginSchema = yup.object().shape({
  email: yup.string().email('Email tidak valid').required('Email wajib diisi'),
  password: yup.string().min(6, 'Password minimal 6 karakter').required('Password wajib diisi'),
});

const forgotSchema = yup.object().shape({
  email: yup.string().email('Email tidak valid').required('Email wajib diisi'),
});

type LoginForm = yup.InferType<typeof loginSchema>;
type ForgotForm = yup.InferType<typeof forgotSchema>;

function AuthPage() {
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const router = useRouter();
  const authStore = useAuthStore((state) => state);
  const authMutation = useMutationLogin();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (sessionStorage.getItem('auth_session_expired')) {
        sessionStorage.removeItem('auth_session_expired');
        toast.error('Sesi Anda telah berakhir, silakan login kembali.', {
          id: 'auth-session-expired',
          duration: 4000,
        });
      }
    }
  }, []);

  const loginForm = useForm<LoginForm>({
    resolver: yupResolver(loginSchema),
  });

  const forgotForm = useForm<ForgotForm>({
    resolver: yupResolver(forgotSchema),
  });

  const handleLogin = async (data: LoginForm) => {
    setErrorMsg(null);
    try {
      const loginData = await authMutation.mutateAsync({
        email: data.email,
        password: data.password,
      });

      resetSessionExpired();
      localStorage.setItem(apiConfig.auth.storageTokenKeyName, loginData.token);
      localStorage.setItem(apiConfig.auth.storageUserKeyName, JSON.stringify(loginData.user));
      authStore.setLogin(loginData?.user);

      toast.success('Berhasil masuk');
      setIsNavigating(true);
      const returnUrl = (router.query?.returnUrl as string) || '/apps';
      router.push(returnUrl);
    } catch (e: any) {
      setErrorMsg(e?.response?.data?.message || 'Login gagal. Periksa kembali kredensial Anda.');
    }
  };

  const handleForgot = async (data: ForgotForm) => {
    setErrorMsg(null);
    // TODO: Connect this to missi-fe's actual forgot password API if available.
    // Simulating API call for now.
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setSuccessMsg('Link reset password telah dikirim. Silakan cek email Anda.');
    } catch (e: any) {
      setErrorMsg('Gagal mengirim link reset password.');
    }
  };

  const loading = authMutation.isPending || isNavigating;

  return (
    <>
      <Head>
        <title>Login — Missi Track</title>
        <meta name="description" content="Sign in to Missi Track maritime vessel tracking platform." />
      </Head>
      <div className="landing relative grid min-h-screen w-full grid-cols-1 overflow-hidden bg-background text-foreground lg:grid-cols-[1.1fr_1fr]">
        {/* LEFT — cinematic ocean visual */}
        <div className="relative hidden overflow-hidden lg:block">
          <video
            src="/videos/hero-video.mp4"
            autoPlay
            loop
            muted
            playsInline
            poster="/images/hero-bg.jpg"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(135deg, oklch(0.12 0.04 240 / 0.55) 0%, oklch(0.12 0.04 240 / 0.4) 40%, oklch(0.12 0.04 240 / 0.85) 100%)',
            }}
          />
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(60% 50% at 30% 30%, oklch(0.55 0.18 220 / 0.25), transparent 70%)',
            }}
          />

          {/* Yacht accent image bottom-right */}
          <img
            src="/images/yacht-side.jpg"
            alt=""
            aria-hidden
            className="pointer-events-none absolute -right-10 bottom-10 hidden h-64 w-auto rounded-2xl border border-white/10 object-cover opacity-90 shadow-2xl xl:block"
          />

          {/* Top brand */}
          <Link
            href="/"
            className="absolute top-8 left-10 z-10 inline-flex items-center gap-2.5 text-white"
          >
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-white p-1 text-primary-foreground shadow-lg shadow-primary/30">
              <img src="/images/logo-missi.png" alt="Missi Track" className="h-full w-full object-contain" />
            </span>
            <span className="font-display text-lg tracking-wide font-bold">Missi Track</span>
          </Link>

          {/* Headline */}
          <div className="relative z-10 flex h-full flex-col justify-end p-10 xl:p-14 text-white">
            <div className="max-w-lg space-y-5">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-widest backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Maritime Intelligence Platform
              </span>
              <h2 className="font-display text-5xl leading-[1.02] tracking-tight xl:text-6xl text-white">
                Navigate
                <br />
                every horizon
                <br />
                with confidence.
              </h2>
              <p className="max-w-md text-base text-white/75">
                Pantau armada Anda secara real-time, kelola pelayaran, dan akses
                intelijen maritim — kapan saja, di mana saja.
              </p>
            </div>

            {/* Mini stats */}
            <div className="mt-10 grid max-w-md grid-cols-3 gap-4 border-t border-white/10 pt-6 text-white/80">
              {[
                { k: '500+', v: 'Vessels' },
                { k: '120+', v: 'Companies' },
                { k: '99.9%', v: 'Uptime' },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-display text-2xl text-white">{s.k}</div>
                  <div className="text-xs uppercase tracking-wider text-white/60">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT — login panel */}
        <div className="relative flex min-h-screen flex-col bg-slate-50/50 dark:bg-background">
          {/* Mobile background */}
          <div className="absolute inset-0 lg:hidden">
            <img
              src="/images/fleet-ocean.jpg"
              alt=""
              aria-hidden
              className="h-full w-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/85 to-background/95" />
          </div>

          {/* Back link (mobile + desktop) */}
          <div className="relative z-10 flex items-center justify-between px-6 py-5 lg:px-12">
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground transition hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Kembali
            </Link>
            <div className="flex items-center gap-2.5 text-foreground lg:hidden">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-white p-1 text-primary-foreground shadow-sm">
                <img src="/images/logo-missi.png" alt="Missi Track" className="h-full w-full object-contain" />
              </span>
              <span className="font-display text-sm tracking-wide font-bold">
                Missi Track
              </span>
            </div>
          </div>

          <div className="relative z-10 my-auto flex flex-1 items-center justify-center px-4 py-6 sm:px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="card-surface w-full max-w-md border border-border/60 bg-white/95 p-6 shadow-xl backdrop-blur-md rounded-3xl sm:p-8 md:p-10"
            >
              <h1 className="font-display text-3xl tracking-tight text-foreground md:text-4xl">
                Welcome back
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {mode === 'login'
                  ? 'Masuk ke panel kontrol armada Anda.'
                  : 'Masukkan email untuk reset password.'}
              </p>

              {errorMsg && (
                <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {errorMsg}
                </div>
              )}
              {successMsg && (
                <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  {successMsg}
                </div>
              )}

              {mode === 'login' ? (
                <form
                  onSubmit={loginForm.handleSubmit(handleLogin)}
                  className="mt-6 space-y-5"
                >
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Email
                    </label>
                    <input
                      type="email"
                      placeholder="nama@perusahaan.com"
                      {...loginForm.register('email')}
                      className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-foreground placeholder-muted-foreground/45 transition focus:border-primary/60 focus:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                    {loginForm.formState.errors.email && (
                      <p className="mt-1 text-xs text-red-500">
                        {loginForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        {...loginForm.register('password')}
                        className="w-full rounded-xl border border-border bg-white px-4 py-3 pr-11 text-sm text-foreground placeholder-muted-foreground/45 transition focus:border-primary/60 focus:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary/20"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground/60 transition hover:text-foreground"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {loginForm.formState.errors.password && (
                      <p className="mt-1 text-xs text-red-500">
                        {loginForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        className="h-4 w-4 rounded border-border bg-white text-primary focus:ring-primary/40"
                      />
                      Ingat saya
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setMode('forgot');
                        setErrorMsg(null);
                        setSuccessMsg(null);
                      }}
                      className="text-sm font-semibold text-primary transition hover:text-primary/80"
                    >
                      Lupa password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition hover:bg-primary/90 disabled:opacity-60"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Sign in'
                    )}
                  </button>
                </form>
              ) : (
                <form
                  onSubmit={forgotForm.handleSubmit(handleForgot)}
                  className="mt-6 space-y-5"
                >
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Email
                    </label>
                    <input
                      type="email"
                      placeholder="nama@perusahaan.com"
                      {...forgotForm.register('email')}
                      className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-foreground placeholder-muted-foreground/45 transition focus:border-primary/60 focus:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                    {forgotForm.formState.errors.email && (
                      <p className="mt-1 text-xs text-red-500">
                        {forgotForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={forgotForm.formState.isSubmitting}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition hover:bg-primary/90 disabled:opacity-60"
                  >
                    {forgotForm.formState.isSubmitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Kirim link reset'
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setMode('login');
                      setErrorMsg(null);
                      setSuccessMsg(null);
                    }}
                    className="flex w-full items-center justify-center text-sm text-muted-foreground transition hover:text-foreground"
                  >
                    Kembali ke Login
                  </button>
                </form>
              )}

              <p className="mt-8 text-center text-xs text-muted-foreground">
                Butuh akses?{' '}
                <Link href="/#contact" className="text-primary hover:underline font-semibold">
                  Request demo
                </Link>
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}

AuthPage.guestGuard = false;
AuthPage.authGuard = false;

export default AuthPage;

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      returnUrl: typeof search.returnUrl === 'string' ? search.returnUrl : undefined,
    };
  },
  beforeLoad: ({ search }: any) => {
    if (typeof window !== 'undefined') {
      const token = window.localStorage.getItem(apiConfig.auth.storageTokenKeyName);
      if (token) {
        const dest = search?.returnUrl || '/apps';
        throw redirect({ to: dest as any });
      }
    }
  },
  component: AuthPage,
});
